import React, { useCallback, useMemo, useState } from 'react';
import { UploadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import { getIdMap } from 'ontraccr-common/lib/Common';

import BorderlessButton from '../../../../common/buttons/BorderlessButton';
import HistoryIcon from '../../../../common/icons/files/HistoryIcon';
import OntraccrTag from '../../../../common/OntraccrTag';
import TagRender from '../../../../common/TagRender';
import Colors from '../../../../constants/Colors';
import { getFormattedCurrency, getPhaseCostcodeKey } from '../../../../forms/formHelpers';
import { currencyFormatter } from '../../../../helpers/inputParsers';
import {
  LABOR_CATEGORY_ID,
  MATERIAL_CATEGORY_ID,
  OVERHEAD_CATEGORY_ID,
} from '../../../../helpers/costcodeHelpers';
import {
  getFormattedHoursLabel,
  showCostUpdatesIcon,
} from '../../../ProjectProgress/progressHelpers';
import ProgressMaterialUpdate from '../../../ProjectProgress/ProgressMaterialUpdate';
import ProjectSpendUpdate from '../../../ProjectProgress/ProjectSpendUpdate';
import CostCodeSelectionTable from '../CostCodeSelectionTable';
import CostTrackingBudgetProgressModal from './CostTrackingBudgetProgressModal';
import CostTrackingCostUpdatesModal from './CostTrackingCostUpdatesModal';
import ProgressRow from '../../../ProjectProgress/ProgressRow';
import { PROGRESS_ROW_TYPE_ACTIVITY } from '../../../ProjectProgress/progressConstants';
import ExpandIcon from '../../../../common/icons/files/ExpandIcon';
import Permissions from '../../../../auth/Permissions';

const formatCurrency = (value) => (value != null && value !== '' ? currencyFormatter(value) : '-');

export default function CostTrackingBudgetProgressTable({
  dataSource,
  emptyText,
  onUploadInvoice,
  onHistoryClick,
  showBilling,
  averageWage = 0,
  scrollY,
}) {
  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');
  const [expandedProgressRecord, setExpandedProgressRecord] = useState(null);
  const [costUpdatesRecord, setCostUpdatesRecord] = useState(null);

  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const projectCostcodeDetailsMap = useSelector((state) => state.projects.projectCostcodeDetails);
  const progresses = useSelector((state) => state.reports.progress);
  const users = useSelector((state) => state.users.users);

  const userMap = useMemo(() => getIdMap(users), [users]);

  const closeProgressModal = useCallback(() => setExpandedProgressRecord(null), []);
  const closeCostUpdatesModal = useCallback(() => setCostUpdatesRecord(null), []);

  const columns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val, record) => (record.isChildRow ? '' : (val ?? '-')),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (val, record) => {
        if (record.isPhaseRow) return '-';
        return record.isChildRow ? '' : (val ?? '-');
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (val, record) => (record.isChildRow ? '' : (val ?? '-')),
    },
    {
      title: 'Category',
      dataIndex: 'categoryId',
      key: 'category',
      width: 120,
      render: (_, record) => {
        const { categoryId, isParentRow, isPhaseRow } = record;
        if (isParentRow || isPhaseRow) {
          return (
            <OntraccrTag
              style={{
                fontSize: 12,
                fontFamily: 'open-sans-600',
                color: Colors.GRAY_950,
              }}
            >
              {record.isPhaseRow ? 'Phase' : 'Multi'}
            </OntraccrTag>
          );
        }
        if (!categoryId) return '-';
        return (
          <TagRender
            tagMap={categoriesIdMap}
            tagIds={[categoryId]}
            useRedesign
          />
        );
      },
    },
    {
      title: 'Est. Hours',
      dataIndex: 'hours',
      key: 'hours',
      width: 100,
      render: (_, record) => {
        const { isPhaseRow, children = [] } = record;
        if (isPhaseRow) {
          const totalHours = children?.reduce((s, c) => s + (c.categoryAssignment?.hours ?? 0), 0);
          return totalHours;
        }
        const hours = record.categoryAssignment?.hours ?? record.hours;
        return hours ?? '-';
      },
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      width: 110,
      render: (_, record) => {
        const { isPhaseRow, children = [] } = record;
        if (isPhaseRow) {
          const totalEstimatedCost = children?.reduce((s, c) => (
            s + (c.categoryAssignment?.estimatedCost ?? 0)
          ), 0);
          return formatCurrency(totalEstimatedCost);
        }
        const value = record.categoryAssignment?.estimatedCost ?? 0;
        return formatCurrency(value);
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      width: 250,
      render: (_, record) => {
        const {
          isParentRow,
          projectId,
          phaseId,
          id: costcodeId,
          activeCategoryId,
          isPhaseRow,
        } = record;
        if (isParentRow) return '-';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ProgressRow
              type={PROGRESS_ROW_TYPE_ACTIVITY}
              isStacked={false}
              projectId={projectId}
              phaseId={phaseId}
              costcodeId={costcodeId}
              displayMode={!hasWrite}
              hasWrite={hasWrite}
              activeCategoryId={activeCategoryId}
            />
            {!isPhaseRow && (
              <div style={{ padding: 0, margin: 0 }}>
                <BorderlessButton
                  iconNode={<ExpandIcon width={15} height={15} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedProgressRecord(record);
                  }}
                  style={{ padding: '10px 10px 0px 10px' }}
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actual Hours',
      dataIndex: 'actualHours',
      key: 'actualHours',
      width: 110,
      render: (_, record) => {
        if (record.isPhaseRow) return '-';
        const { isParentRow, categoryId } = record;
        const clockable = (
          categoryId === LABOR_CATEGORY_ID
          || categoryId === OVERHEAD_CATEGORY_ID
          || categoriesIdMap[categoryId]?.allowTimeTracking
        );

        if (!isParentRow && !clockable) return '-';

        return (
          getFormattedHoursLabel({
            costcodeDetailsMap: projectCostcodeDetailsMap,
            costcodeId: record.id,
            phaseId: record.phaseId,
          })
        );
      },
    },
    {
      title: 'Actual Spend',
      dataIndex: 'actualSpend',
      key: 'actualSpend',
      width: 110,
      render: (_, record) => {
        const {
          isParentRow,
          actualSpend,
          isPhaseRow,
          children = [],
        } = record;
        if (isParentRow) return formatCurrency(actualSpend);
        if (isPhaseRow) {
          const totalSpend = children?.reduce((s, c) => s + (c.actualSpend ?? 0), 0);
          return formatCurrency(totalSpend);
        }
        return (
          <ProjectSpendUpdate
            projectId={record.projectId}
            phaseId={record.phaseId}
            costcodeId={record.id}
            hasWrite={hasWrite}
            activeCategoryId={record.activeCategoryId}
          />
        );
      },
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 110,
      render: (_, record) => {
        const { isPhaseRow, children = [] } = record;
        if (isPhaseRow) {
          const totalCost = children?.reduce((s, c) => s + (c.totalCost ?? 0), 0);
          return formatCurrency(totalCost);
        }
        return formatCurrency(record.totalCost);
      },
    },
    {
      title: 'Remaining Budget',
      dataIndex: 'remainingBudget',
      key: 'remainingBudget',
      width: 130,
      render: (_, record) => {
        const { isPhaseRow, children = [] } = record;
        if (isPhaseRow) {
          const remainingBudget = children?.reduce((s, c) => (
            s + ((c.categoryAssignment?.estimatedCost ?? 0) - (c.totalCost ?? 0))
          ), 0);
          return formatCurrency(remainingBudget);
        }
        const estimatedCost = record.categoryAssignment?.estimatedCost ?? 0;
        return formatCurrency(estimatedCost - (record.totalCost ?? 0));
      },
    },
    {
      title: 'Qty. Used',
      dataIndex: 'quantityUsed',
      key: 'quantityUsed',
      width: 150,
      render: (_, record) => {
        if (record.isPhaseRow) return '-';
        const { isParentRow, categoryId } = record;
        const canTrackQuantity = (
          categoryId === MATERIAL_CATEGORY_ID
          || categoriesIdMap[categoryId]?.allowQuantityTracking
        );

        if (isParentRow || !canTrackQuantity) return '-';

        const { categoryAssignment } = record;
        const { estimatedQuantity, units } = categoryAssignment;

        return (
          <ProgressMaterialUpdate
            projectId={record.projectId}
            phaseId={record.phaseId}
            costcodeId={record.id}
            units={units}
            estimatedQuantity={estimatedQuantity}
            hasWrite={hasWrite}
            activeCategoryId={record.activeCategoryId}
          />
        );
      },
    },
    ...(showBilling ? [
      {
        title: 'Total Billing Amount',
        dataIndex: 'totalBillingAmount',
        key: 'totalBillingAmount',
        width: 120,
      },
      {
        title: 'Avg. Billing Rate',
        dataIndex: 'avgBillingRate',
        key: 'avgBillingRate',
        width: 120,
      },
    ] : []),
    {
      title: 'Committed Cost',
      dataIndex: 'committedCost',
      key: 'committedCost',
      width: 120,
      render: (_, record) => {
        if (record.isPhaseRow || record.isChildRow) return '-';
        const { phaseId, id: costcodeId } = record;
        const phaseCostcodeKey = getPhaseCostcodeKey(phaseId, costcodeId);
        const { committedCost } = projectCostcodeDetailsMap[phaseCostcodeKey] || {};
        return getFormattedCurrency(committedCost);
      },
    },
    {
      title: 'Updates',
      dataIndex: 'costUpdates',
      key: 'costUpdates',
      width: 60,
      render: (_, record) => (
        showCostUpdatesIcon(record) ? (
          <BorderlessButton
            iconNode={<UnorderedListOutlined style={{ fontSize: 16, color: Colors.GRAY_800 }} />}
            onClick={(e) => {
              e.stopPropagation();
              setCostUpdatesRecord(record);
            }}
          />
        ) : '-'
      ),
    },
    {
      title: 'History',
      dataIndex: 'history',
      key: 'history',
      width: 60,
      render: (_, record) => (
        <BorderlessButton
          iconNode={<HistoryIcon width={13} height={13} />}
          onClick={(e) => {
            e.stopPropagation();
            onHistoryClick(record);
          }}
        />
      ),
    },
    ...(onUploadInvoice
      ? [{
        title: 'Upload',
        dataIndex: 'upload',
        key: 'upload',
        width: 60,
        render: (_, record) => (
          record.isPhaseRow ? '-' : (
            <BorderlessButton
              iconNode={<UploadOutlined style={{ fontSize: 16, color: Colors.GRAY_800 }} />}
              onClick={() => onUploadInvoice(record)}
            />
          )
        ),
      }]
      : []),
  ], [
    categoriesIdMap,
    onUploadInvoice,
    projectCostcodeDetailsMap,
    onHistoryClick,
    showBilling,
  ]);

  return (
    <>
      <CostCodeSelectionTable
        columns={columns}
        dataSource={dataSource}
        rowKey="rowKey"
        emptyText={emptyText || 'No cost codes to show'}
        scrollY={scrollY}
        expandable={{
          rowExpandable: (record) => (record.children?.length ?? 0) > 0,
        }}
      />
      <Modal
        visible={!!expandedProgressRecord}
        onCancel={closeProgressModal}
        footer={null}
        width="50vw"
        destroyOnClose
        centered
        bodyStyle={{
          padding: '36px 42px',
          margin: 0,
        }}
      >
        {expandedProgressRecord && (
          <CostTrackingBudgetProgressModal
            record={expandedProgressRecord}
            projectCostcodeDetailsMap={projectCostcodeDetailsMap}
            userMap={userMap}
            progresses={progresses}
            categoriesIdMap={categoriesIdMap}
            averageWage={averageWage}
          />
        )}
      </Modal>
      <CostTrackingCostUpdatesModal
        visible={!!costUpdatesRecord}
        record={costUpdatesRecord}
        costUpdates={costUpdatesRecord?.costUpdates ?? []}
        onClose={closeCostUpdatesModal}
      />
    </>
  );
}

CostTrackingBudgetProgressTable.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.shape({
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
  })),
  emptyText: PropTypes.string,
  scrollY: PropTypes.number,
  onUploadInvoice: PropTypes.func,
  onHistoryClick: PropTypes.func,
  showBilling: PropTypes.bool,
  averageWage: PropTypes.number,
};

CostTrackingBudgetProgressTable.defaultProps = {
  dataSource: [],
  emptyText: null,
  scrollY: undefined,
  onUploadInvoice: null,
  onHistoryClick: null,
  showBilling: false,
  averageWage: 0,
};
